import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface WhitelabelConfig {
  id: string
  customer_id: string
  customer_name: string
  enabled: boolean
  branding: {
    company_name: string
    logo_url: string | null
    favicon_url: string | null
    primary_color: string
    secondary_color: string
    accent_color: string
    background_color: string
    text_color: string
  }
  features: {
    hide_powered_by: boolean
    custom_login_page: boolean
    custom_email_templates: boolean
    custom_domain: boolean
    custom_footer: boolean
  }
  email_settings: {
    from_name: string
    from_email: string
    reply_to: string
  } | null
  custom_css: string
  created_at: string
  updated_at: string
}

interface Theme {
  id: string
  name: string
  description: string
  is_default: boolean
  colors: Record<string, string>
}

export default function WhitelabelPage() {
  const [configurations, setConfigurations] = useState<WhitelabelConfig[]>([])
  const [themes, setThemes] = useState<Theme[]>([])
  const [statistics, setStatistics] = useState<any>(null)
  const [selectedConfig, setSelectedConfig] = useState<WhitelabelConfig | null>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [configRes, themeRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/whitelabel/configurations'),
      fetch(API_BASE + '/whitelabel/themes'),
      fetch(API_BASE + '/whitelabel/statistics')
    ])
    if (configRes.ok) setConfigurations((await configRes.json()).configurations || [])
    if (themeRes.ok) setThemes((await themeRes.json()).themes || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">White-label Configuration</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ New Configuration</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Configs</div>
              <div className="text-2xl font-bold">{statistics.total_configurations}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Enabled</div>
              <div className="text-2xl font-bold text-green-600">{statistics.enabled_configurations}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Custom Domain</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.with_custom_domain}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Custom Email</div>
              <div className="text-2xl font-bold">{statistics.with_custom_email}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Custom CSS</div>
              <div className="text-2xl font-bold">{statistics.with_custom_css}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Themes</div>
              <div className="text-2xl font-bold">{statistics.total_themes}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 space-y-6">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Configurations</h2></div>
              <div className="divide-y">
                {configurations.map(config => (
                  <div key={config.id} className="p-4 hover:bg-gray-50 cursor-pointer" onClick={() => setSelectedConfig(config)}>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="font-semibold">{config.branding.company_name}</div>
                        <div className="text-sm text-gray-500">{config.customer_name}</div>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${config.enabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                        {config.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-1">
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: config.branding.primary_color }}></div>
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: config.branding.secondary_color }}></div>
                        <div className="w-4 h-4 rounded" style={{ backgroundColor: config.branding.accent_color }}></div>
                      </div>
                      <div className="flex gap-2 text-xs text-gray-500">
                        {config.features.custom_domain && <span className="px-2 py-0.5 bg-gray-100 rounded">Domain</span>}
                        {config.features.custom_login_page && <span className="px-2 py-0.5 bg-gray-100 rounded">Login</span>}
                        {config.features.custom_email_templates && <span className="px-2 py-0.5 bg-gray-100 rounded">Email</span>}
                        {config.features.hide_powered_by && <span className="px-2 py-0.5 bg-gray-100 rounded">No Branding</span>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Available Themes</h2></div>
              <div className="grid grid-cols-3 gap-4 p-6">
                {themes.map(theme => (
                  <div key={theme.id} className="p-4 border rounded-lg">
                    <div className="flex justify-between items-start mb-2">
                      <div className="font-medium">{theme.name}</div>
                      {theme.is_default && <span className="px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">Default</span>}
                    </div>
                    <p className="text-sm text-gray-500 mb-3">{theme.description}</p>
                    <div className="flex gap-1">
                      {Object.values(theme.colors).slice(0, 6).map((color, i) => (
                        <div key={i} className="w-6 h-6 rounded" style={{ backgroundColor: color }}></div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div>
            {selectedConfig ? (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">{selectedConfig.branding.company_name}</h3>

                <div className="space-y-4">
                  <div>
                    <div className="text-sm text-gray-500 mb-2">Brand Colors</div>
                    <div className="grid grid-cols-4 gap-2">
                      <div>
                        <div className="w-full h-8 rounded" style={{ backgroundColor: selectedConfig.branding.primary_color }}></div>
                        <div className="text-xs text-center mt-1">Primary</div>
                      </div>
                      <div>
                        <div className="w-full h-8 rounded" style={{ backgroundColor: selectedConfig.branding.secondary_color }}></div>
                        <div className="text-xs text-center mt-1">Secondary</div>
                      </div>
                      <div>
                        <div className="w-full h-8 rounded" style={{ backgroundColor: selectedConfig.branding.accent_color }}></div>
                        <div className="text-xs text-center mt-1">Accent</div>
                      </div>
                      <div>
                        <div className="w-full h-8 rounded border" style={{ backgroundColor: selectedConfig.branding.background_color }}></div>
                        <div className="text-xs text-center mt-1">Background</div>
                      </div>
                    </div>
                  </div>

                  <div className="border-t pt-4">
                    <div className="text-sm text-gray-500 mb-2">Features</div>
                    <div className="space-y-2">
                      {Object.entries(selectedConfig.features).map(([key, val]) => (
                        <div key={key} className="flex justify-between text-sm">
                          <span className="capitalize">{key.replace(/_/g, ' ')}</span>
                          <span className={val ? 'text-green-600' : 'text-gray-400'}>{val ? 'Yes' : 'No'}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {selectedConfig.email_settings && (
                    <div className="border-t pt-4">
                      <div className="text-sm text-gray-500 mb-2">Email Settings</div>
                      <div className="text-sm space-y-1">
                        <div>From: {selectedConfig.email_settings.from_name}</div>
                        <div className="text-gray-500">{selectedConfig.email_settings.from_email}</div>
                      </div>
                    </div>
                  )}

                  <div className="border-t pt-4">
                    <div className="text-xs text-gray-400">
                      Last updated: {new Date(selectedConfig.updated_at).toLocaleDateString()}
                    </div>
                  </div>

                  <button className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">Edit Configuration</button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">Configuration Details</h3>
                <p className="text-gray-500 text-sm">Select a configuration to view and edit details</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  )
}
